import { z } from "zod";
import { createPhoneSchema } from "./phone";
const defaultValidationMessages = {
    required: "This field is required",
    invalidEmail: "Invalid email address",
    invalidUserName: "Choose a valid username.",
    minLength: "Minimum length is {{min}} characters",
    passwordUppercase: "Password must contain at least one uppercase letter",
    passwordLowercase: "Password must contain at least one lowercase letter",
    passwordNumber: "Password must contain at least one number",
    passwordSpecial: "Password must contain at least one special character",
    passwordsDoNotMatch: "Passwords do not match",
    invalidMfaCode: "MFA code must contain exactly 6 numbers",
    requiredMfaCode: "MFA code is required",
};
function defaultValidationTranslate(key, params) {
    const template = defaultValidationMessages[key] ?? key;
    if (!params)
        return template;
    return Object.entries(params).reduce((message, [paramKey, value]) => message.replace(new RegExp(`{{${paramKey}}}`, "g"), String(value)), template);
}
// Mirror of ASP.NET Identity User.AllowedUserNameCharacters in apps/api ServiceCollectionExtensions.cs; keep in sync.
export const USERNAME_ALLOWED_CHARACTERS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-._@+!#$%&'*/=?^`{|}~";
const usernameAllowedSet = new Set(USERNAME_ALLOWED_CHARACTERS);
export function isValidUserName(value) {
    return [...value].every((character) => usernameAllowedSet.has(character));
}
export function createPasswordSchema(t = defaultValidationTranslate) {
    return z
        .string()
        .min(8, t("minLength", { min: 8 }))
        .regex(/[A-Z]/, t("passwordUppercase"))
        .regex(/[a-z]/, t("passwordLowercase"))
        .regex(/\d/, t("passwordNumber"))
        .regex(/[^A-Za-z0-9]/, t("passwordSpecial"));
}
export function createEmailSchema(t = defaultValidationTranslate) {
    return z.email(t("invalidEmail"));
}
export function createMfaCodeSchema(t = defaultValidationTranslate) {
    return z.object({
        code: z
            .string()
            .min(1, t("requiredMfaCode"))
            .length(6, t("invalidMfaCode"))
            .regex(/^\d{6}$/, t("invalidMfaCode")),
    });
}
export function createLoginSchema(t = defaultValidationTranslate) {
    return z.object({
        usernameOrEmail: z
            .string()
            .min(1, t("required"))
            .refine((value) => !value.includes("@") || z.email().safeParse(value).success, t("invalidEmail")),
        password: z.string().min(6, t("minLength", { min: 6 })),
    });
}
export function createRegisterSchema(t = defaultValidationTranslate) {
    return z
        .object({
        email: createEmailSchema(t),
        firstName: z.string().min(1, t("required")),
        lastName: z.string().min(1, t("required")),
        phoneNumber: createPhoneSchema(t, { required: false }),
        emergencyContactName: z.string().optional(),
        emergencyContactPhone: createPhoneSchema(t, { required: false }),
        password: createPasswordSchema(t),
        confirmPassword: z.string(),
    })
        .refine((data) => data.password === data.confirmPassword, {
        message: t("passwordsDoNotMatch"),
        path: ["confirmPassword"],
    });
}
export function createCreateUserSchema(t = defaultValidationTranslate) {
    return z.object({
        email: z.email(t("invalidEmail")),
        password: createPasswordSchema(t),
        role: z.enum(["User", "Admin", "Owner", "SuperAdmin"]).optional(),
        firstName: z.string().min(1, t("required")),
        lastName: z.string().min(1, t("required")),
    });
}
export function createUpdateUserSchema(t = defaultValidationTranslate) {
    return z.object({
        userName: z
            .string()
            .min(2, t("minLength", { min: 2 }))
            .refine(isValidUserName, t("invalidUserName"))
            .optional(),
        email: z.email(t("invalidEmail")).optional(),
        password: createPasswordSchema(t).optional(),
        role: z.enum(["User", "Admin", "Owner", "SuperAdmin"]).optional(),
        firstName: z.string().min(1, t("required")).optional(),
        lastName: z.string().min(1, t("required")).optional(),
        phoneNumber: createPhoneSchema(t, { required: false }),
        dateOfBirth: z.string().optional(),
        gender: z.enum(["male", "female"]).optional(),
        emergencyContactName: z.string().optional(),
        emergencyContactPhone: createPhoneSchema(t, { required: false }),
        fitnessGoals: z.string().optional(),
        preferredWorkoutTime: z.string().optional(),
    });
}
export const passwordSchema = createPasswordSchema();
export const mfaCodeSchema = createMfaCodeSchema();
export const loginSchema = createLoginSchema();
export const registerSchema = createRegisterSchema();
export const createUserSchema = createCreateUserSchema();
export const updateUserSchema = createUpdateUserSchema();
